import { readKalkylfilRawCell } from '@shared/helpers/kalkylfil-csv.parser';
import { formatImportTableCellDisplay } from './import-table-cell-display.helpers';

/**
 * Automatic column-width sizing for the imported-rows metadata strip
 * (DEV-3298). Ported from the DEV-3144 POC; rewired to plain `raw` row
 * objects instead of element `importRowMetadata`.
 */

/** Sample size for automatic column widths (value rows only, not headers). */
export const IMPORT_COLUMN_SAMPLE_ROWS = 20;

const MIN_COLUMN_PX = 28;
const MAX_COLUMN_PX = 560;
const HORIZONTAL_PADDING_PX = 22;

const MEASURE_FAMILY = 'system-ui,-apple-system,BlinkMacSystemFont,sans-serif';

/** Data cells in {@link MetadataStrip} (~10px, normal weight). */
const MEASURE_FONT_CELL = `400 10px ${MEASURE_FAMILY}`;

/** Header labels in {@link MetadataStrip} (~10px, semibold). */
const MEASURE_FONT_HEADER = `600 10px ${MEASURE_FAMILY}`;

let measureCanvas: HTMLCanvasElement | null = null;

const getMeasureContext = (): CanvasRenderingContext2D | null => {
  if (typeof document === 'undefined') return null;
  measureCanvas ??= document.createElement('canvas');
  return measureCanvas.getContext('2d');
};

export type MeasureImportTextOptions = {
  /** Matches MetadataStrip header row when `600`. */
  weight?: 400 | 600;
};

/** Max text width in px; char estimate when canvas is unavailable (tests). */
export const measureImportCellContentWidthPx = (
  text: string,
  options: MeasureImportTextOptions = {},
): number => {
  const weight = options.weight ?? 400;
  const ctx = getMeasureContext();
  if (!ctx) return text.length * (weight === 600 ? 6.5 : 6.2);
  ctx.font = weight === 600 ? MEASURE_FONT_HEADER : MEASURE_FONT_CELL;
  return ctx.measureText(text).width;
};

export const clampImportColumnWidthPx = (width: number): number =>
  Math.min(MAX_COLUMN_PX, Math.max(MIN_COLUMN_PX, Math.ceil(width)));

const cellDisplayString = (value: unknown): string => {
  if (value === undefined || value === null) return '';
  return formatImportTableCellDisplay(String(value));
};

const displayHeaderLabel = (
  header: string,
  headerLabels: Record<string, string> | undefined,
): string => headerLabels?.[header] ?? header;

export type ComputeImportColumnWidthsOptions = {
  /** Original CSV labels from {@link buildHeaderLabels}; widens auto-size. */
  headerLabels?: Record<string, string>;
};

/**
 * Column widths from sample row values and visible header labels (first
 * {@link IMPORT_COLUMN_SAMPLE_ROWS} rows; header row uses semibold measure).
 */
export const computeImportColumnWidths = (
  headers: readonly string[],
  rows: readonly Record<string, string | number>[],
  options: ComputeImportColumnWidthsOptions = {},
): number[] => {
  const { headerLabels } = options;
  const sample = rows.slice(0, IMPORT_COLUMN_SAMPLE_ROWS);
  const maxByHeader = new Map<string, number>();
  for (const h of headers) {
    const label = displayHeaderLabel(h, headerLabels);
    maxByHeader.set(h, measureImportCellContentWidthPx(label, { weight: 600 }));
  }
  for (const row of sample) {
    for (const h of headers) {
      const w = measureImportCellContentWidthPx(
        cellDisplayString(readKalkylfilRawCell(row, h)),
      );
      if (w > (maxByHeader.get(h) ?? 0)) maxByHeader.set(h, w);
    }
  }
  return headers.map((h) =>
    clampImportColumnWidthPx((maxByHeader.get(h) ?? 0) + HORIZONTAL_PADDING_PX),
  );
};
